/*
  quick-add.js
  Fixes:
  1. Keeps quick-add modal opening correctly.
  2. Prevents media-gallery setActiveMedia crash inside quick-add modal.
  3. Re-syncs selected variant ID and Add to Cart button after variant changes.
*/

(() => {
  function installSafeMediaGalleryPatch() {
    const MediaGallery = customElements.get('media-gallery');

    if (!MediaGallery || MediaGallery.prototype.__quickAddSafeSetActiveMedia) return;

    const originalSetActiveMedia = MediaGallery.prototype.setActiveMedia;

    if (typeof originalSetActiveMedia !== 'function') return;

    MediaGallery.prototype.setActiveMedia = function(mediaId, prepend) {
      if (!mediaId || !this.elements || !this.elements.viewer) return;

      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);

      /*
        This guard prevents the quick-add modal from crashing when the selected
        variant points to a media ID that does not exist inside the injected modal gallery.
      */
      if (!activeMedia) return;

      return originalSetActiveMedia.call(this, mediaId, prepend);
    };

    MediaGallery.prototype.__quickAddSafeSetActiveMedia = true;
  }

  if (customElements.get('media-gallery')) {
    installSafeMediaGalleryPatch();
  } else if (customElements.whenDefined) {
    customElements.whenDefined('media-gallery').then(installSafeMediaGalleryPatch).catch(() => {});
  }
})();

if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        if (cartNotification) cartNotification.setActiveElement(this.openedBy);

        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;

        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');

        const openerSpinner = opener.querySelector('.loading-overlay__spinner');
        if (openerSpinner) openerSpinner.classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');

            this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');

            if (!this.productElement) return;

            this.preventDuplicatedIDs();
            this.removeDOMElements();

            const mediaGallery = this.productElement.querySelector('media-gallery');
            if (mediaGallery) mediaGallery.dataset.disablePrepend = 'false';

            this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) {
              window.ProductModel.loadShopifyXR();
            }

            this.removeGalleryListSemantic();
            this.updateImageSizes();
            this.preventVariantURLSwitching();

            super.show(opener);

            this.bindQuickAddVariantSync();
          })
          .catch((error) => {
            console.error('Quick add modal failed to load:', error);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');

            const openerSpinner = opener.querySelector('.loading-overlay__spinner');
            if (openerSpinner) openerSpinner.classList.add('hidden');
          });
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;

        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');

          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });

          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      preventVariantURLSwitching() {
        const variantPicker = this.modalContent.querySelector('variant-radios, variant-selects');
        if (!variantPicker) return;

        variantPicker.setAttribute('data-update-url', 'false');
      }

      removeDOMElements() {
        const pickupAvailability = this.productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = this.productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const stickyAtc = this.productElement.querySelector('sticky-atc');
        if (stickyAtc) stickyAtc.remove();

        const modalDialogs = this.productElement.querySelectorAll('modal-dialog');
        modalDialogs.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;

        if (!sectionId) return;

        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${sectionId}`);

        this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
          element.dataset.originalSection = sectionId;
        });
      }

      removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      bindQuickAddVariantSync() {
        const variantPicker = this.modalContent.querySelector('variant-radios, variant-selects');
        const productForm = this.modalContent.querySelector('product-form');
        const form = this.modalContent.querySelector('form[action*="/cart/add"], form[data-type="add-to-cart-form"]');

        if (!form) return;

        const sync = () => {
          requestAnimationFrame(() => this.syncQuickAddVariantState());
          setTimeout(() => this.syncQuickAddVariantState(), 80);
          setTimeout(() => this.syncQuickAddVariantState(), 250);
          setTimeout(() => this.syncQuickAddVariantState(), 500);
          setTimeout(() => this.syncQuickAddVariantState(), 900);
        };

        if (variantPicker) {
          variantPicker.addEventListener('change', sync);
          variantPicker.addEventListener('input', sync);
          variantPicker.addEventListener('click', sync);
        }

        if (productForm) {
          productForm.addEventListener('change', sync);
          productForm.addEventListener('input', sync);
          productForm.addEventListener('click', sync);
        }

        this.modalContent.addEventListener('change', sync);
        this.modalContent.addEventListener('input', sync);
        this.modalContent.addEventListener('click', (event) => {
          const target = event.target;

          if (!(target instanceof Element)) return;

          if (
            target.matches('input[type="radio"], label, select') ||
            target.closest('.product-form__input')
          ) {
            sync();
          }
        });

        sync();
      }

      getQuickAddVariants() {
        const variantPicker = this.modalContent.querySelector('variant-radios, variant-selects');
        if (!variantPicker) return [];

        const script = variantPicker.querySelector('script[type="application/json"]');
        if (!script) return [];

        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          return [];
        }
      }

      getQuickAddSelectedOptions() {
        const variantPicker = this.modalContent.querySelector('variant-radios, variant-selects');
        if (!variantPicker) return [];

        const selectedOptions = [];

        variantPicker.querySelectorAll('.product-form__input').forEach((group) => {
          const checkedInput = group.querySelector('input[type="radio"]:checked');
          const select = group.querySelector('select');

          if (checkedInput) {
            selectedOptions.push(checkedInput.value);
          } else if (select) {
            selectedOptions.push(select.value);
          }
        });

        return selectedOptions;
      }

      getQuickAddCurrentVariant() {
        const variants = this.getQuickAddVariants();
        const selectedOptions = this.getQuickAddSelectedOptions();

        if (!variants.length || !selectedOptions.length) return null;

        return variants.find((variant) => {
          return selectedOptions.every((optionValue, index) => {
            return String(variant.options[index]) === String(optionValue);
          });
        });
      }

      syncQuickAddVariantState() {
        const form = this.modalContent.querySelector('form[action*="/cart/add"], form[data-type="add-to-cart-form"]');
        if (!form) return;

        const variant = this.getQuickAddCurrentVariant();
        if (!variant) return;

        const idInput = form.querySelector('input[name="id"]');
        const submitButton = form.querySelector('[name="add"]');
        const submitLabel = submitButton && submitButton.querySelector('.main-atc__label__text');
        const priceLabel = submitButton && submitButton.querySelector('.main-atc-price');
        const spinner = submitButton && submitButton.querySelector('.loading-overlay__spinner');

        if (idInput) {
          idInput.value = variant.id;
          idInput.disabled = false;
        }

        if (!submitButton) return;

        submitButton.classList.remove('loading');

        if (spinner) {
          spinner.classList.add('hidden');
        }

        const addToCartText =
          (window.variantStrings && window.variantStrings.addToCart) ||
          (window.theme && window.theme.strings && window.theme.strings.addToCart) ||
          'Add to cart';

        const soldOutText =
          (window.variantStrings && window.variantStrings.soldOut) ||
          (window.theme && window.theme.strings && window.theme.strings.soldOut) ||
          'Sold out';

        if (variant.available) {
          submitButton.disabled = false;
          submitButton.removeAttribute('disabled');
          submitButton.removeAttribute('aria-disabled');
          submitButton.removeAttribute('data-unavailable');

          if (submitLabel) {
            submitLabel.textContent = addToCartText;
          }

          if (priceLabel) {
            priceLabel.classList.remove('hidden');
          }
        } else {
          submitButton.disabled = true;
          submitButton.setAttribute('disabled', 'disabled');
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.setAttribute('data-unavailable', 'true');

          if (submitLabel) {
            submitLabel.textContent = soldOutText;
          }

          if (priceLabel) {
            priceLabel.classList.add('hidden');
          }
        }

        const secondaryButton = this.modalContent.querySelector(
          '[data-skip-cart-button="true"], #SectionAtcBtn-' + (this.productElement && this.productElement.dataset ? this.productElement.dataset.section : '')
        );

        if (secondaryButton) {
          if (variant.available) {
            secondaryButton.disabled = false;
            secondaryButton.removeAttribute('disabled');
            secondaryButton.removeAttribute('aria-disabled');
          } else {
            secondaryButton.disabled = true;
            secondaryButton.setAttribute('disabled', 'disabled');
            secondaryButton.setAttribute('aria-disabled', 'true');
          }
        }
      }

      updateImageSizes() {
        const product = this.modalContent.querySelector('.product');
        if (!product) return;

        const desktopColumns = product.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
